'use client'

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'

interface OffMarketMember {
  id: string
  email: string
  name: string | null
}

interface OffMarketAuthContextValue {
  isAuthenticated: boolean
  member: OffMarketMember | null
  loading: boolean
  signIn: (email: string) => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
}

const OffMarketAuthContext = createContext<OffMarketAuthContextValue>({
  isAuthenticated: false,
  member: null,
  loading: true,
  signIn: async () => ({ success: false }),
  signOut: async () => {},
})

export function useOffMarketAuth() {
  return useContext(OffMarketAuthContext)
}

export function OffMarketAuthProvider({ children }: { children: ReactNode }) {
  const [member, setMember] = useState<OffMarketMember | null>(null)
  const [loading, setLoading] = useState(true)

  // Check session on mount
  useEffect(() => {
    fetch('/api/off-market-auth')
      .then((res) => res.json())
      .then((data) => {
        if (data.authenticated) {
          setMember(data.member)
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const signIn = useCallback(async (email: string) => {
    try {
      const res = await fetch('/api/off-market-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      const data = await res.json()

      // Registered email → signed in immediately (session cookie set by the API).
      if (res.ok && data.success) {
        setMember(data.member)
        return { success: true }
      }

      if (data.error === 'not_found') {
        return { success: false, error: 'not_found' }
      }

      return { success: false, error: data.error || 'Sign in failed' }
    } catch {
      return { success: false, error: 'Network error' }
    }
  }, [])

  const signOut = useCallback(async () => {
    await fetch('/api/off-market-auth', { method: 'DELETE' })
    setMember(null)
  }, [])

  return (
    <OffMarketAuthContext.Provider
      value={{
        isAuthenticated: !!member,
        member,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </OffMarketAuthContext.Provider>
  )
}
